import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Settings, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';

// Debounced Input for Search
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 800,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

const Options = () => {
  const [activeTab, setActiveTab] = useState('reasons');
  const [loading, setLoading] = useState(false);

  // Pagination state for both tabs
  const [reasonsPagination, setReasonsPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [partsPagination, setPartsPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [reasonsRowCount, setReasonsRowCount] = useState(0);
  const [partsRowCount, setPartsRowCount] = useState(0);
  const [reasonsFilter, setReasonsFilter] = useState('');
  const [partsFilter, setPartsFilter] = useState('');

  // Pause Reasons State
  const [pauseReasons, setPauseReasons] = useState([]);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [reasonForm, setReasonForm] = useState({ reason_name: '', reason_description: '' });
  const [reasonSaving, setReasonSaving] = useState(false);

  // Parts State
  const [parts, setParts] = useState([]);
  const [showPartModal, setShowPartModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [partForm, setPartForm] = useState({ part_name: '', category: '', unit: '' });
  const [partCategories, setPartCategories] = useState([]);
  const [partSaving, setPartSaving] = useState(false);

  // Get bearer token for API calls
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // ========== PAUSE REASONS CRUD ==========

  const loadPauseReasons = useCallback(async (searchTerm = reasonsFilter) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', reasonsPagination.pageIndex + 1);
      params.append('pageSize', reasonsPagination.pageSize);
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(`${API_BASE_URL}/api/mms/pause-reasons?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.status === 1) {
        const data = response.data.data;
        setPauseReasons(data.reasons || []);
        setReasonsRowCount(data.count || 0);
      }
    } catch (err) {
      toast.error('Failed to load pause reasons');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [reasonsPagination, reasonsFilter]);

  const handleSaveReason = async () => {
    if (!reasonForm.reason_name.trim()) {
      toast.error('Reason name is required');
      return;
    }

    try {
      setReasonSaving(true);
      if (editingReason) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/api/mms/pause-reasons/${editingReason.reason_id}`,
          reasonForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Pause reason updated successfully');
      } else {
        // Create new
        await axios.post(
          `${API_BASE_URL}/api/mms/pause-reasons`,
          reasonForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Pause reason created successfully');
      }
      setShowReasonModal(false);
      setReasonForm({ reason_name: '', reason_description: '' });
      setEditingReason(null);
      await loadPauseReasons();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save reason');
      console.error(err);
    } finally {
      setReasonSaving(false);
    }
  };

  const handleDeleteReason = async (reasonId) => {
    if (!window.confirm('Are you sure you want to delete this reason?')) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/mms/pause-reasons/${reasonId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Pause reason deleted successfully');
      await loadPauseReasons();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete reason');
      console.error(err);
    }
  };

  const openReasonModal = (reason = null) => {
    if (reason) {
      setEditingReason(reason);
      setReasonForm({
        reason_name: reason.reason_name,
        reason_description: reason.reason_description || ''
      });
    } else {
      setEditingReason(null);
      setReasonForm({ reason_name: '', reason_description: '' });
    }
    setShowReasonModal(true);
  };

  // ========== PARTS CRUD ==========

  const loadParts = useCallback(async (searchTerm = partsFilter) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', partsPagination.pageIndex + 1);
      params.append('limit', partsPagination.pageSize);
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axios.get(`${API_BASE_URL}/api/mms/parts?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.status === 1) {
        const data = response.data.data;
        setParts(data.parts || []);
        setPartsRowCount(data.total || 0);
      }
    } catch (err) {
      toast.error('Failed to load parts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [partsPagination, partsFilter]);

  const loadPartCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/mms/parts/categories`, {
        headers: getAuthHeaders()
      });
      if (response.data.status === 1) {
        setPartCategories(response.data.data.categories || []);
      }
    } catch (err) {
      console.error('Failed to load part categories:', err);
    }
  };

  const handleSavePart = async () => {
    if (!partForm.part_name.trim()) {
      toast.error('Part name is required');
      return;
    }

    try {
      setPartSaving(true);
      if (editingPart) {
        // Update existing
        await axios.put(
          `${API_BASE_URL}/api/mms/parts/${editingPart.part_id}`,
          partForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Part updated successfully');
      } else {
        // Create new
        await axios.post(
          `${API_BASE_URL}/api/mms/parts`,
          partForm,
          { headers: getAuthHeaders() }
        );
        toast.success('Part created successfully');
      }
      setShowPartModal(false);
      setPartForm({ part_name: '', category: '', unit: '' });
      setEditingPart(null);
      await loadParts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save part');
      console.error(err);
    } finally {
      setPartSaving(false);
    }
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm('Are you sure you want to delete this part?')) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/mms/parts/${partId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Part deleted successfully');
      await loadParts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete part');
      console.error(err);
    }
  };

  const openPartModal = (part = null) => {
    if (part) {
      setEditingPart(part);
      setPartForm({
        part_name: part.part_name,
        category: part.category || '',
        unit: part.unit || ''
      });
    } else {
      setEditingPart(null);
      setPartForm({ part_name: '', category: '', unit: '' });
    }
    setShowPartModal(true);
  };

  // Load data on tab change or pagination/filter change
  useEffect(() => {
    if (activeTab === 'reasons') {
      loadPauseReasons(reasonsFilter);
    } else if (activeTab === 'parts') {
      loadParts(partsFilter);
      loadPartCategories();
    }
  }, [activeTab, reasonsPagination, partsPagination, reasonsFilter, partsFilter, loadPauseReasons, loadParts]);

  // Handle search for reasons
  const handleReasonsSearch = useCallback((searchTerm) => {
    setReasonsFilter(searchTerm);
    setReasonsPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Handle search for parts
  const handlePartsSearch = useCallback((searchTerm) => {
    setPartsFilter(searchTerm);
    setPartsPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Pagination helpers
  const reasonsTotalPages = Math.ceil(reasonsRowCount / reasonsPagination.pageSize);
  const partsTotalPages = Math.ceil(partsRowCount / partsPagination.pageSize);

  if (loading && pauseReasons.length === 0 && parts.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" style={{ borderTopColor: 'transparent', borderRight: '3px solid currentColor', borderBottom: '3px solid currentColor', borderLeft: '3px solid currentColor', borderRadius: '50%' }}></div>
              <p className="text-gray-600 font-medium">Loading options...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-2 py-1">
        {/* Professional Header Card - Following SupplierGroups Pattern */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}>
                <Settings className="h-5 w-5" style={{ color: '#3B82F6' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">MMS Options</h1>
                <p className="text-xs text-gray-500 mb-1">
                  Manage pause reasons and spare parts inventory
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#3B82F6' }}>
                    {activeTab === 'reasons' 
                      ? `Showing ${Math.min(reasonsPagination.pageSize, pauseReasons.length)} of ${reasonsRowCount} results`
                      : `Showing ${Math.min(partsPagination.pageSize, parts.length)} of ${partsRowCount} results`}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: All Controls */}
            <div className="flex flex-col gap-3 items-end min-w-0">
              {/* Row 1: Tab Buttons and Create Button */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setActiveTab('reasons')}
                  className={`px-3 py-1.5 border rounded-md text-sm font-medium transition-all ${
                    activeTab === 'reasons'
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Pause Reasons
                </button>
                <button
                  onClick={() => setActiveTab('parts')}
                  className={`px-3 py-1.5 border rounded-md text-xs font-medium transition-all ${
                    activeTab === 'parts'
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Spare Parts
                </button>

                <div className="h-6 border-l border-gray-200"></div>

                <button
                  onClick={() => activeTab === 'reasons' ? openReasonModal() : openPartModal()}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-shrink-0 transition-all bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {activeTab === 'reasons' ? 'Create Reason' : 'Create Part'}
                </button>

                <button
                  onClick={() => activeTab === 'reasons' ? loadPauseReasons(reasonsFilter) : loadParts(partsFilter)}
                  disabled={loading}
                  className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-xs rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex-shrink-0 disabled:opacity-50 transition-colors"
                  title="Refresh"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#3B82F6' : 'currentColor'}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>

                {/* Page Size Selector */}
                <select
                  value={activeTab === 'reasons' ? reasonsPagination.pageSize : partsPagination.pageSize}
                  onChange={(e) => {
                    const pageSize = Number(e.target.value);
                    if (activeTab === 'reasons') {
                      setReasonsPagination(prev => ({ ...prev, pageIndex: 0, pageSize }));
                    } else {
                      setPartsPagination(prev => ({ ...prev, pageIndex: 0, pageSize }));
                    }
                  }}
                  className="text-xs px-2 py-1.5 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 focus:border-gray-500"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              {/* Search Box - Full width on second row */}
              <div className="relative w-full mt-3">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <DebouncedInput
                  value={activeTab === 'reasons' ? reasonsFilter : partsFilter}
                  onChange={activeTab === 'reasons' ? handleReasonsSearch : handlePartsSearch}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-xs"
                  placeholder={activeTab === 'reasons' ? 'Search reasons...' : 'Search parts...'}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Content - Cards Grid */}
        <div className="container mx-auto px-0">
        {/* PAUSE REASONS TAB */}
        {activeTab === 'reasons' && (
          <div>
            {pauseReasons.length === 0 ? (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center shadow-sm">
                <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No pause reasons</h3>
                <p className="text-gray-600">Get started by adding your first pause reason</p>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {pauseReasons.map((reason) => (
                    <div key={reason.reason_id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow hover:border-blue-200 transition-all duration-200 flex flex-col h-full">
                      {/* Header with Reason Name */}
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-lg text-blue-600 truncate">
                          {reason.reason_name}
                        </h3>
                      </div>

                      {/* Flexible content area */}
                      <div className="flex-1 mb-2">
                        {/* Compact Description */}
                        <div className="space-y-0.5 text-xs text-gray-600">
                          <div className="flex items-start">
                            <svg className="w-2.5 h-2.5 mr-1 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="line-clamp-3">
                              {reason.reason_description || 'No description available'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 pt-2 border-t border-gray-100 mt-auto">
                        <button
                          onClick={() => openReasonModal(reason)}
                          className="flex-1 inline-flex items-center justify-center px-2 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                          title="Edit"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteReason(reason.reason_id)}
                          className="flex-1 inline-flex items-center justify-center px-2 py-1.5 border border-red-300 bg-white hover:bg-red-50 text-red-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                          title="Delete"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination Controls */}
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-600">
                    Page {reasonsPagination.pageIndex + 1} of {reasonsTotalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setReasonsPagination(prev => ({ ...prev, pageIndex: Math.max(0, prev.pageIndex - 1) }))}
                      disabled={reasonsPagination.pageIndex === 0}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setReasonsPagination(prev => ({ ...prev, pageIndex: Math.min(reasonsTotalPages - 1, prev.pageIndex + 1) }))}
                      disabled={reasonsPagination.pageIndex >= reasonsTotalPages - 1}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SPARE PARTS TAB */}
        {activeTab === 'parts' && (
          <div>
            {parts.length === 0 ? (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-12 text-center shadow-sm">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No spare parts</h3>
                <p className="text-gray-600">Get started by adding your first spare part</p>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {parts.map((part) => (
                    <div key={part.part_id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow hover:border-blue-200 transition-all duration-200 flex flex-col h-full">
                      {/* Header with Part Name */}
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-lg text-blue-600 truncate">
                          {part.part_name}
                        </h3>
                      </div>

                      {/* Flexible content area */}
                      <div className="flex-1 mb-2">
                        {/* Compact Description */}
                        <div className="space-y-0.5 text-xs text-gray-600">
                          <div className="flex items-start">
                            <svg className="w-2.5 h-2.5 mr-1 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="line-clamp-3">
                              {part.description || 'No description available'}
                            </span>
                          </div>
                          
                          {part.category && (
                            <div className="flex items-center">
                              <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9m0 0h6m-6 0V5a2 2 0 012-2h4a2 2 0 012 2v4" />
                              </svg>
                              <span className="truncate">Category: {part.category}</span>
                            </div>
                          )}

                          {part.unit && (
                            <div className="flex items-center">
                              <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                              <span className="truncate">Unit: {part.unit}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1 pt-2 border-t border-gray-100 mt-auto">
                        <button
                          onClick={() => openPartModal(part)}
                          className="flex-1 inline-flex items-center justify-center px-2 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                          title="Edit"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                          </svg>
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeletePart(part.part_id)}
                          className="flex-1 inline-flex items-center justify-center px-2 py-1.5 border border-red-300 bg-white hover:bg-red-50 text-red-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                          title="Delete"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Pagination Controls */}
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
                  <span className="text-xs text-gray-600">
                    Page {partsPagination.pageIndex + 1} of {partsTotalPages}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setPartsPagination(prev => ({ ...prev, pageIndex: Math.max(0, prev.pageIndex - 1) }))}
                      disabled={partsPagination.pageIndex === 0}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setPartsPagination(prev => ({ ...prev, pageIndex: Math.min(partsTotalPages - 1, prev.pageIndex + 1) }))}
                      disabled={partsPagination.pageIndex >= partsTotalPages - 1}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Modals */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-md">
              <h3 className="font-semibold text-gray-900">
                {editingReason ? 'Edit Pause Reason' : 'Add Pause Reason'}
              </h3>
              <button onClick={() => setShowReasonModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Reason Name *
                </label>
                <input
                  type="text"
                  value={reasonForm.reason_name}
                  onChange={(e) => setReasonForm({ ...reasonForm, reason_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Waiting for Parts"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={reasonForm.reason_description}
                  onChange={(e) => setReasonForm({ ...reasonForm, reason_description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional description"
                  rows="3"
                />
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end gap-2 bg-white rounded-b-md">
              <button
                onClick={() => setShowReasonModal(false)}
                disabled={reasonSaving}
                className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReason}
                disabled={reasonSaving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              >
                {reasonSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Part Modal */}
      {showPartModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center rounded-t-md">
              <h3 className="font-semibold text-gray-900">
                {editingPart ? 'Edit Spare Part' : 'Add Spare Part'}
              </h3>
              <button onClick={() => setShowPartModal(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Part Name *
                </label>
                <input
                  type="text"
                  value={partForm.part_name}
                  onChange={(e) => setPartForm({ ...partForm, part_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Water Pump"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  list="categories"
                  value={partForm.category}
                  onChange={(e) => setPartForm({ ...partForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Pumps"
                />
                <datalist id="categories">
                  {partCategories.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Unit of Measure
                </label>
                <input
                  type="text"
                  value={partForm.unit}
                  onChange={(e) => setPartForm({ ...partForm, unit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., pcs, meters, liters"
                />
              </div>
            </div>
            <div className="border-t border-gray-200 p-4 flex justify-end gap-2 bg-white rounded-b-md">
              <button
                onClick={() => setShowPartModal(false)}
                disabled={partSaving}
                className="px-4 py-2 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePart}
                disabled={partSaving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
              >
                {partSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Options;
